import { iterableFilteredEntities } from './constants';

export type FullDocStyleData = {
  bodyClasses: string;
  headStyles: string;
  headStylesheets: string[];
};

const headTags = ['title', 'style', 'base', 'link', 'meta', 'script', 'noscript'];

/**
 * finds all of the styles in the head style elements and concatinates them into a single string
 * @constructor
 * @param {string} html the source html (full html document)
 * @returns {FullDocStyleData} all of the style data extracted from the <head> and <body>
 */
export const getStylesFullDoc = (html: string): FullDocStyleData => {
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(html, 'text/html'); // will force existance of <html>, <head>, and <body>
  const head = htmlDoc.getElementsByTagName('head');
  // get the <head> styles
  const styleTags = head[0].getElementsByTagName('style');
  const headStyles: string[] = [];
  Array.from(styleTags).forEach((styleElt) => {
    headStyles.push(styleElt.textContent || '');
  });
  // get the <head> linked stylesheets
  const linkTags = head[0].getElementsByTagName('link');
  const headStylesheets: string[] = [];
  Array.from(linkTags).forEach((linkElt) => {
    const hrefAttr = linkElt.getAttribute('href');
    const isStylesheet = linkElt.getAttribute('rel') === 'stylesheet';
    const isScreenMedia = !['print', 'speech'].includes(linkElt.getAttribute('media') || '');
    if (isStylesheet && isScreenMedia && !!hrefAttr) {
      headStylesheets.push(hrefAttr);
    }
  });
  // get the <body> classes and styles
  const body = htmlDoc.getElementsByTagName('body');
  const bodyClasses = body[0].getAttribute('class') || '';
  const bodyStyles = body[0].getAttribute('style');
  // there is no configuration for <body> style, so just append it to the other styles
  if (bodyStyles) {
    headStyles.push(`body { ${bodyStyles} }`);
  }

  return {
    bodyClasses,
    headStyles: headStyles.join('\n'),
    headStylesheets,
  };
};

/**
 * determines if the string contains any full document html tags
 * @constructor
 * @param {string} html the source html (full html document)
 * @returns {boolean} indication if the string contains any full document html tags
 */
export const hasHtmlFullDocTags = (html: string) => {
  const fullDocTags = ['html', 'body', 'head'];
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(html, 'text/html'); // will force existance of <html>, <head>, and <body>
  const hasHeadTags = headTags.some((tag) => !!htmlDoc.getElementsByTagName(tag).length);
  const htmlLc = html.toLocaleLowerCase();
  const hasDocTags = fullDocTags.some((tag) => !!htmlLc.includes(`<${tag}`));
  return hasHeadTags || hasDocTags;
};

/**
 * re-encodes the entities in a string into their html entity name form
 * @constructor
 * @param {string} html the source html
 * @returns {string} the html containing designated entities
 */
export const preserveEntities = (html: string) => {
  return html.replace(/([\u007E-\u20AC])/g, (match, capturedChar) => {
    const charDec = capturedChar.charCodeAt(); // UTF-16 decimal
    const htmlEntityName = iterableFilteredEntities.find(
      (entity) => entity.unicode === charDec,
    )?.name;
    return htmlEntityName ? `&${htmlEntityName};` : capturedChar;
  });
};

/**
 * attempts to auto-correct the html by letting the browser render it to a div
 * @constructor
 * @param {string} html the source html (body content only)
 * @returns {string} the cleaned/repaired html (body content only)
 */
export const repairHTMLBody = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;

  return div.innerHTML;
};

/**
 * attempts to auto-correct the html by running it through a DOMParser
 * @constructor
 * @param {string} html the source html (full html document)
 * @returns {string} the cleaned/repaired html (full html document)
 */
export const repairHTMLFullDoc = (html: string) => {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const htmlDoc = parser.parseFromString(html, 'text/html'); // will force existance of <html>, <head>, and <body>
  // promote headTags to the head (or Tiny will delete all but link and style)
  const head = htmlDoc.getElementsByTagName('head');
  const body = htmlDoc.getElementsByTagName('body');
  headTags.forEach((headTag) => {
    const tags = body[0].getElementsByTagName(headTag);
    Array.from(tags).forEach((tag) => {
      head[0].appendChild(tag);
    });
  });
  // the serializer returns entities as characters, so we need to preserve the entities handled by Tiny so the diff appears accurate
  return preserveEntities(serializer.serializeToString(htmlDoc));
};
